'use client';

import React, { useRef, useState } from 'react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { toast } from '@/core/utils/toast';
import { fetchAPI } from '@/core/api/client';
import { trackLead } from '@/core/utils/analytics';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import {
    buildContactPayload,
    CONTACT_COUNTRY_CODES,
    CONTACT_FIELD_PRESETS,
    contactFields,
    type ContactField,
} from './contact-fields';

interface ContactFormSettings {
    title?: string;
    subtitle?: string;
    buttonText?: string;
    successMessage?: string;
    bg?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    padding?: number;
    titleColor?: string;
    titleSize?: number;
    labelColor?: string;
    buttonBg?: string;
    buttonTextColor?: string;
    fields?: ContactField[];
}

interface ContactFormProps {
    settings?: ContactFormSettings;
    styles?: any;
    viewport?: 'desktop' | 'tablet' | 'mobile';
}

export const ContactForm: React.FC<ContactFormProps> = ({ settings: rawSettings = {}, styles, viewport }) => {
    const { locale, t, tPath } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    // Time-to-submit for the backend's bot check (forms_router.MIN_FILL_MS).
    const renderedAt = useRef(Date.now());

    // Sizing fields (borderWidth/borderRadius/padding/titleSize) are responsive — merged over
    // the flat settings here so every existing settings.X read below picks up the per-device
    // value automatically, falling back to the old flat key for already-configured pages.
    const currentStyles = { ...(styles || {}), ...(viewport ? (styles || {})[viewport] || {} : {}) };
    const settings = { ...rawSettings, ...currentStyles };
    const fields = contactFields(settings);
    const countryNames = React.useMemo(() => {
        try {
            return new Intl.DisplayNames([locale], { type: 'region' });
        } catch {
            return null;
        }
    }, [locale]);

    const fieldLabel = (field: ContactField) => {
        const preset = CONTACT_FIELD_PRESETS[field.fieldType];
        return field.label || t(`contact.${field.fieldType}_label`, preset.labelFallback);
    };

    const fieldPlaceholder = (field: ContactField) => {
        const preset = CONTACT_FIELD_PRESETS[field.fieldType];
        return field.placeholder || t(`contact.${field.fieldType}_placeholder`, preset.placeholderFallback || '');
    };

    const countryName = (code: string) => countryNames?.of(code) || code;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const form = e.currentTarget;
        if (!form.checkValidity()) {
            const firstInvalid = form.querySelector<HTMLElement>(':invalid');
            // A required dropdown is validated through the hidden native <select> the Select
            // component keeps for the form — that one can't take focus, its visible trigger can.
            const focusTarget = firstInvalid?.tagName === 'SELECT' && firstInvalid.getAttribute('aria-hidden') === 'true'
                ? form.querySelector<HTMLElement>(`[data-select-name="${firstInvalid.getAttribute('name')}"]`)
                : firstInvalid;
            focusTarget?.focus();
            toast.error(t('contact.error_required_fields', 'Uzupełnij wymagane pola formularza.'));
            return;
        }

        setIsSubmitting(true);

        const data = new FormData(form);
        const values = Object.fromEntries(fields.map((field) => [field.id, String(data.get(field.id) || '')]));

        try {
            await fetchAPI('/forms/submit', {
                method: 'POST',
                body: JSON.stringify({
                    ...buildContactPayload(fields, values, fieldLabel, countryName),
                    website: String(data.get('company_website') || '') || undefined,
                    elapsed_ms: Date.now() - renderedAt.current,
                    source_path: window.location.pathname,
                }),
            });
            trackLead('contact');
            toast.success(settings.successMessage || t('contact.success_default', 'Wiadomość została wysłana! Skontaktujemy się z Tobą wkrótce.'));
            form.reset();
            setResetKey((key) => key + 1); // dropdowns keep their own state — remount them empty
        } catch {
            toast.error(t('contact.error_generic', 'Nie udało się wysłać wiadomości. Spróbuj ponownie później.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const wrapperStyle: React.CSSProperties = {
        backgroundColor: settings.bg,
        borderColor: settings.borderColor,
        borderWidth: settings.borderColor ? (settings.borderWidth ?? 1) : undefined,
        borderStyle: settings.borderColor ? 'solid' : undefined,
        borderRadius: settings.borderRadius !== undefined ? `${settings.borderRadius}px` : undefined,
        padding: settings.padding !== undefined ? `${settings.padding}px` : undefined,
    };

    const renderConsentLabel = (field: ContactField) => {
        const label = fieldLabel(field);
        if (!field.linkText || !field.href || !label.includes(field.linkText)) return label;
        const [before, ...after] = label.split(field.linkText);
        return (
            <>
                {before}
                <a className="underline underline-offset-2 hover:no-underline" href={tPath(field.href)}>
                    {field.linkText}
                </a>
                {after.join(field.linkText)}
            </>
        );
    };

    const renderField = (field: ContactField) => {
        const preset = CONTACT_FIELD_PRESETS[field.fieldType];
        const label = fieldLabel(field);
        const placeholder = fieldPlaceholder(field);
        const common = {
            id: field.id,
            name: field.id,
            required: Boolean(field.required),
            autoComplete: preset.autoComplete,
        };

        if (preset.input === 'checkbox') {
            return (
                <label htmlFor={field.id} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed" style={{ color: settings.labelColor }}>
                    <input {...common} type="checkbox" value="on" className="mt-1 size-4 shrink-0 rounded border-gray-300 accent-current" />
                    <span>{renderConsentLabel(field)}{field.required ? <span aria-hidden="true"> *</span> : null}</span>
                </label>
            );
        }

        let control: React.ReactNode;
        if (preset.input === 'textarea') {
            control = (
                <textarea
                    {...common}
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder={placeholder}
                />
            );
        } else if (preset.input === 'country' || preset.input === 'select') {
            const options = preset.input === 'country'
                ? CONTACT_COUNTRY_CODES.map((code) => ({ value: code, label: countryName(code) }))
                : (field.options || []).filter(Boolean).map((option) => ({ value: option, label: option }));
            control = (
                <Select key={resetKey} name={common.name} required={common.required} autoComplete={common.autoComplete}>
                    <SelectTrigger
                        id={common.id}
                        data-select-name={common.name}
                        className="bg-transparent text-sm font-normal shadow-sm focus:ring-1 focus:ring-offset-0 data-[placeholder]:text-muted-foreground"
                    >
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            );
        } else {
            control = (
                <Input
                    {...common}
                    type={preset.inputType || 'text'}
                    inputMode={preset.inputMode}
                    placeholder={placeholder}
                />
            );
        }

        return (
            <div className="space-y-2">
                <Label htmlFor={field.id} style={{ color: settings.labelColor }}>
                    {label}{field.required ? <span aria-hidden="true"> *</span> : null}
                </Label>
                {control}
            </div>
        );
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="space-y-4 max-w-lg mx-auto p-6 bg-white rounded-2xl shadow-sm border border-gray-100"
            style={wrapperStyle}
            noValidate
        >
            {/* Honeypot: off-screen and skipped by keyboard and screen readers, so only bots fill
                it in — the backend then drops the submission (forms_router.py). */}
            <input
                type="text"
                name="company_website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] h-px w-px overflow-hidden opacity-0"
            />
            <div>
                <h3
                    className="text-xl font-bold text-gray-900 mb-1"
                    style={{ color: settings.titleColor, fontSize: settings.titleSize ? `${settings.titleSize}px` : undefined }}
                >
                    {settings.title || t('contact.title_default', 'Napisz do nas')}
                </h3>
                {settings.subtitle && (
                    <p className="text-sm text-gray-500" style={{ color: settings.labelColor }}>{settings.subtitle}</p>
                )}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {fields.map((field) => (
                    <div key={field.id} className={field.width === 'half' ? '' : 'md:col-span-2'}>
                        {renderField(field)}
                    </div>
                ))}
            </div>
            <Button
                type="submit"
                className="w-full font-bold h-12"
                style={settings.buttonBg ? { backgroundColor: settings.buttonBg, color: settings.buttonTextColor ?? '#ffffff' } : undefined}
                disabled={isSubmitting}
            >
                {isSubmitting ? t('contact.sending', 'Wysyłanie...') : (settings.buttonText || t('contact.submit_default', 'Wyślij wiadomość'))}
            </Button>
        </form>
    );
};
